import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import type { MyFeedbackRow } from '@ai-meeting/shared'
import { clampToMaxLength, QUICK_FEEDBACK_COMMENT_MAX_LENGTH } from '@/constants/meeting'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { fetchMyFeedback, submitFeedback } from '@/services/feedback'
import { formatDateTime } from '@/utils/formatDateTime'
import { RobotIcon, SendIcon, XMarkIcon } from './Icons'

/** Matches the live meeting page path, capturing the meeting id in group 1. */
const LIVE_MEETING_PATH_PATTERN = /^\/meetings\/([^/]+)\/live$/

/**
 * Only the live meeting page ("/meetings/:id/live") should tag feedback with
 * a meeting id — `currentMeetingId` in the store reflects the "last viewed"
 * meeting and stays stale after navigating back to Home/History, so it can't
 * be used to detect "currently in a live meeting".
 *
 * @param pathname Current route pathname.
 * @returns The live meeting's id, or `undefined` when not on that page.
 */
function getLiveMeetingIdFromPath(pathname: string): string | undefined {
  return LIVE_MEETING_PATH_PATTERN.exec(pathname)?.[1]
}

/** Pixel coordinates of the button's top-left corner, relative to the viewport. */
type Position = { x: number; y: number }

/** Where the popover/toast should expand relative to the button. */
type PopoverPlacement = { vertical: 'up' | 'down'; horizontal: 'left' | 'right' }

/** Which panel the popover currently shows. */
type PopoverMode = 'compose' | 'list'

/** `localStorage` key used to persist the button's dragged position across reloads. */
const STORAGE_KEY = 'feedbackButtonPosition'
/** Distance (px) between the button and the viewport edge in its default corner. */
const DEFAULT_MARGIN = 24
/** Pointer movement (px) below which a press is treated as a click, not a drag. */
const DRAG_THRESHOLD = 6
/** Viewport width at/above which the wider desktop margin applies. */
const DESKTOP_BREAKPOINT = 768

/**
 * Keep a dragged position fully inside the current viewport, so resizing the
 * window (or restoring a stale `localStorage` value) never leaves the button
 * stuck off-screen.
 *
 * @param position Candidate top-left coordinates for the button.
 * @param size Button's current rendered width/height.
 * @param viewport Viewport width/height to clamp against.
 * @returns A position guaranteed to keep the whole button within `viewport`.
 */
export function clampPosition(
  position: Position,
  size: { width: number; height: number },
  viewport: { width: number; height: number }
): Position {
  const maxX = Math.max(0, viewport.width - size.width)
  const maxY = Math.max(0, viewport.height - size.height)
  return {
    x: Math.min(Math.max(position.x, 0), maxX),
    y: Math.min(Math.max(position.y, 0), maxY),
  }
}

/**
 * Decide which direction the popover/toast should open so it always stays
 * inside the viewport, regardless of where the user dragged the button to.
 *
 * @param buttonPosition Button's current top-left coordinates.
 * @param viewport Viewport width/height.
 * @returns `vertical`/`horizontal` flags mapped straight to Tailwind classes.
 */
export function computePopoverPlacement(
  buttonPosition: Position,
  viewport: { width: number; height: number }
): PopoverPlacement {
  return {
    vertical: buttonPosition.y > viewport.height / 2 ? 'up' : 'down',
    horizontal: buttonPosition.x > viewport.width / 2 ? 'right' : 'left',
  }
}

/**
 * Resting margin (px) from the viewport edge, matching the button's existing
 * default-corner spacing (narrower on small/touch screens).
 *
 * @param viewportWidth Current viewport width.
 * @returns Margin in px.
 */
function getMargin(viewportWidth: number): number {
  return viewportWidth >= DESKTOP_BREAKPOINT ? DEFAULT_MARGIN : 16
}

/**
 * Snap a just-dropped position to whichever side of the screen it's closer
 * to (BRAI-175), so the button always settles at a screen edge instead of
 * wherever the user happened to release it. Only the X axis snaps; the
 * dropped Y position is preserved so vertical drags still feel free-form.
 *
 * @param droppedPosition Clamped top-left coordinates where the pointer was released.
 * @param size Button's current rendered width/height.
 * @param viewport Viewport width/height.
 * @returns The button's top-left coordinates after snapping to an edge.
 */
export function computeSnapPosition(
  droppedPosition: Position,
  size: { width: number; height: number },
  viewport: { width: number; height: number }
): Position {
  const margin = getMargin(viewport.width)
  const center = droppedPosition.x + size.width / 2
  const x = center > viewport.width / 2 ? viewport.width - size.width - margin : margin
  return { x, y: droppedPosition.y }
}

/**
 * Default bottom-right resting spot (mirrors the old fixed `bottom-4 right-4`
 * / `md:bottom-6 md:right-6` corner) for users who have never dragged the
 * button yet.
 *
 * @param size Button's current rendered width/height.
 * @returns Top-left coordinates for the default corner.
 */
function getDefaultPosition(size: { width: number; height: number }): Position {
  const margin = getMargin(window.innerWidth)
  return {
    x: window.innerWidth - size.width - margin,
    y: window.innerHeight - size.height - margin,
  }
}

/**
 * Read a previously persisted position from `localStorage`, if any.
 *
 * @returns The stored position, or `null` if missing/invalid.
 */
function readStoredPosition(): Position | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') {
      return { x: parsed.x, y: parsed.y }
    }
  } catch {
    // Ignore malformed/inaccessible storage and fall back to the default spot.
  }
  return null
}

/**
 * App-wide floating feedback button + popover (BRAI-185, redesigned in
 * BRAI-175). Rendered via a portal straight into `document.body` so it stays
 * visible on every authenticated page and is never clipped by an ancestor's
 * `overflow-hidden`. The button is freely draggable (Pointer Events, so
 * mouse/touch/pen all work without extra code): its position is clamped to
 * the viewport, snapped to the nearest edge on release, persisted to
 * `localStorage` so it survives navigation and reloads, and re-clamped on
 * window resize. A short pointer movement is treated as a click (opens the
 * popover); anything past the drag threshold is treated as a drag and never
 * toggles the popover. Because the button can now sit anywhere on screen, the
 * popover/tooltip/toasts pick their expansion direction dynamically so they
 * never render off-viewport.
 *
 * The popover has two modes: a blank `compose` form for a first-time
 * submitter, or a read-only `list` of past feedback (fetched lazily on first
 * open) for a returning one, with a "+ New feedback" action to switch back to
 * `compose`. Closing via the header's ✕ (or clicking outside) preserves the
 * draft. A successful submit closes the popover and shows a 3s toast; a
 * failed submit also closes the popover (keeping the draft for a retry) but
 * shows a persistent red toast with a Retry action instead of an inline error.
 */
export default function FeedbackControl() {
  const { t, locale } = useUiStrings()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<PopoverMode>('compose')
  const [comment, setComment] = useState('')
  const [commentError, setCommentError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  const [justSubmitted, setJustSubmitted] = useState(false)
  const [hovering, setHovering] = useState(false)
  const [history, setHistory] = useState<MyFeedbackRow[] | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [position, setPosition] = useState<Position | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const commentRef = useRef(comment)
  const historyFetchedRef = useRef(false)
  /**
   * True while a `pointerdown`→`pointerup` interaction is in flight (or just
   * finished). The browser always fires a trailing `click` after `pointerup`;
   * this flag lets `onClick` tell that "pointer echo" apart from a genuine
   * keyboard-triggered click (Enter/Space with no preceding pointer event),
   * so the popover only toggles once per interaction, and dragging never
   * toggles it at all.
   */
  const pointerInteractionRef = useRef(false)

  useEffect(() => {
    commentRef.current = comment
  }, [comment])

  // Restore a persisted position on mount, clamped to the current viewport
  // in case the screen shrank since it was saved.
  useEffect(() => {
    const stored = readStoredPosition()
    if (!stored || !buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPosition(
      clampPosition(
        stored,
        { width: rect.width, height: rect.height },
        { width: window.innerWidth, height: window.innerHeight }
      )
    )
  }, [])

  // Keep the button on-screen if the viewport shrinks after a position was
  // dragged/persisted.
  useEffect(() => {
    const onResize = () => {
      if (!position || !buttonRef.current) return
      const rect = buttonRef.current.getBoundingClientRect()
      setPosition((current) =>
        current
          ? clampPosition(
              current,
              { width: rect.width, height: rect.height },
              { width: window.innerWidth, height: window.innerHeight }
            )
          : current
      )
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [position])

  useEffect(() => {
    if (!open) return
    const onDoc = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  useEffect(() => {
    if (!justSubmitted) return
    const timer = setTimeout(() => setJustSubmitted(false), 3000)
    return () => clearTimeout(timer)
  }, [justSubmitted])

  /**
   * Picks the popover's starting mode for the current open: `compose` while
   * there's an unsent draft (never yank it away from the user), otherwise
   * `list` when the user has past feedback, else a blank `compose` form.
   */
  const applyModeForOpen = useCallback((rows: MyFeedbackRow[] | null) => {
    if (commentRef.current.trim()) {
      setMode('compose')
      return
    }
    setMode(rows && rows.length > 0 ? 'list' : 'compose')
  }, [])

  /** Fetches (and caches) the user's feedback history for the `list` mode. */
  const loadHistory = useCallback(async (): Promise<MyFeedbackRow[]> => {
    try {
      const rows = await fetchMyFeedback()
      setHistory(rows)
      return rows
    } catch {
      // Non-critical: fall back to an empty list so the popover still opens.
      setHistory([])
      return []
    }
  }, [])

  // On every transition from closed to open: drop any stale toast/hover
  // state, fetch the feedback history exactly once per component lifetime,
  // and (re)pick compose vs. list from the latest data.
  useEffect(() => {
    if (!open) return
    setSubmitFailed(false)
    setHovering(false)

    if (!historyFetchedRef.current) {
      historyFetchedRef.current = true
      setHistoryLoading(true)
      void loadHistory().then((rows) => {
        setHistoryLoading(false)
        applyModeForOpen(rows)
      })
    } else {
      applyModeForOpen(history)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const toggle = () => {
    setOpen((current) => !current)
    setJustSubmitted(false)
  }

  const handleSubmit = async () => {
    if (submitting || !comment.trim()) return
    if (comment.length > QUICK_FEEDBACK_COMMENT_MAX_LENGTH) {
      setCommentError(fmt(t.fieldTooLong, { max: QUICK_FEEDBACK_COMMENT_MAX_LENGTH }))
      document.getElementById('feedback-comment-input')?.focus()
      return
    }
    setCommentError(null)
    setSubmitting(true)
    setSubmitFailed(false)
    try {
      await submitFeedback(comment.trim(), getLiveMeetingIdFromPath(pathname))
      setComment('')
      setJustSubmitted(true)
      setOpen(false)
      // Refresh in the background so the next open already has fresh data.
      void loadHistory()
    } catch {
      setOpen(false)
      setSubmitFailed(true)
    } finally {
      setSubmitting(false)
    }
  }

  /** Submits on Ctrl/Cmd+Enter, mirroring the Send button's disabled rules. */
  const handleTextareaKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault()
      if (!submitting && comment.trim()) void handleSubmit()
    }
  }

  /**
   * Drive the drag gesture from a `pointerdown` on the button. Tracks total
   * pointer travel so a near-stationary press still opens the popover like a
   * regular click, while anything past `DRAG_THRESHOLD` moves the button and
   * skips the toggle entirely.
   */
  const handlePointerDown = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return
    const button = buttonRef.current
    if (!button) return

    pointerInteractionRef.current = true
    const rect = button.getBoundingClientRect()
    const size = { width: rect.width, height: rect.height }
    const offsetX = event.clientX - rect.left
    const offsetY = event.clientY - rect.top
    const startX = event.clientX
    const startY = event.clientY
    let dragged = false

    const onPointerMove = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startX
      const dy = moveEvent.clientY - startY
      if (!dragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      dragged = true
      setIsDragging(true)
      setPosition(
        clampPosition({ x: moveEvent.clientX - offsetX, y: moveEvent.clientY - offsetY }, size, {
          width: window.innerWidth,
          height: window.innerHeight,
        })
      )
    }

    const stopDrag = () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', stopDrag)
      window.removeEventListener('pointercancel', stopDrag)
      setIsDragging(false)

      if (dragged) {
        // A real drag happened: snap to the nearest edge and persist that
        // spot. `pointerInteractionRef` stays true so the trailing `click`
        // (see `onClick` below) is a no-op.
        setPosition((current) => {
          if (!current) return current
          const snapped = computeSnapPosition(current, size, {
            width: window.innerWidth,
            height: window.innerHeight,
          })
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(snapped))
          } catch {
            // Storage may be unavailable (private mode, quota); ignore.
          }
          return snapped
        })
      } else {
        toggle()
      }

      // Fallback for `pointercancel` (no trailing `click` fires in that case):
      // release the flag on the next tick so it doesn't wrongly swallow a
      // later keyboard-triggered click. A same-tick `click` still sees the
      // flag as `true` and resets it itself before this runs.
      setTimeout(() => {
        pointerInteractionRef.current = false
      }, 0)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', stopDrag, { once: true })
    window.addEventListener('pointercancel', stopDrag, { once: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const viewport = { width: window.innerWidth, height: window.innerHeight }
  const effectivePosition = position ?? getDefaultPosition({ width: 48, height: 48 })
  const placement = computePopoverPlacement(effectivePosition, viewport)
  const popoverPositionClass = [
    placement.vertical === 'up' ? 'bottom-full mb-2' : 'top-full mt-2',
    placement.horizontal === 'right' ? 'right-0' : 'left-0',
  ].join(' ')
  // The tooltip opens sideways (BRAI-175 redesign), not stacked above/below
  // like the popover/toasts: it sits vertically centered on the button and
  // expands toward whichever side of the screen has more room.
  const tooltipPositionClass =
    placement.horizontal === 'right' ? 'right-full mr-2' : 'left-full ml-2'

  const headerTitle = mode === 'compose' ? t.feedbackHeaderCompose : t.feedbackHeaderList

  return createPortal(
    <div
      className={[
        position ? 'fixed z-[200]' : 'fixed bottom-4 right-4 z-[200] md:bottom-6 md:right-6',
        // Animate position changes (e.g. a resize re-clamp, or the post-drag
        // snap-to-edge) smoothly, but never while actively dragging — a
        // transition there would make the button visibly lag behind the pointer.
        isDragging ? '' : 'transition-[left,top] duration-150',
      ]
        .filter(Boolean)
        .join(' ')}
      style={position ? { left: position.x, top: position.y } : undefined}
      ref={containerRef}
    >
      <button
        type="button"
        ref={buttonRef}
        onPointerDown={handlePointerDown}
        onClick={() => {
          // A pointer interaction (click or drag) already ran `toggle()` (or
          // deliberately skipped it, for a drag) inside the `pointerup`
          // handler above; the `click` firing right after is just its echo,
          // so swallow it here. Only a click with no preceding pointer event
          // — i.e. a keyboard-triggered Enter/Space activation — reaches
          // `toggle()` through this handler.
          if (pointerInteractionRef.current) {
            pointerInteractionRef.current = false
            return
          }
          toggle()
        }}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        disabled={open}
        aria-expanded={open}
        aria-label={t.feedbackLabel}
        title={t.feedbackLabel}
        style={{ touchAction: 'none' }}
        className={[
          'flex h-12 w-12 select-none items-center justify-center rounded-full',
          isDragging ? 'cursor-grabbing' : 'cursor-grab transition-colors',
          open
            ? 'bg-[var(--bg-tertiary)] opacity-40'
            : 'bg-transparent hover:bg-[var(--bg-tertiary)]',
        ].join(' ')}
      >
        <RobotIcon className="h-12 w-12 shrink-0" />
      </button>

      {open && (
        <div
          className={`absolute ${popoverPositionClass} z-[200] w-80 animate-fade-in rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 shadow-[var(--shadow-lg)]`}
        >
          <div className="mb-2 flex items-center gap-2 border-b border-[var(--border-color)] pb-2">
            <RobotIcon className="h-5 w-5 shrink-0" />
            <span className="flex-1 text-sm font-semibold text-[var(--text-secondary)]">
              {headerTitle}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t.close}
              className="rounded p-1 text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {historyLoading ? (
            // Only the very first-ever open hits this (the history fetch is
            // lazy + cached): avoids flashing the compose form before
            // jumping to the list once a returning user's data arrives.
            <p className="py-2 text-sm text-[var(--text-secondary)]">{t.loading}</p>
          ) : mode === 'compose' ? (
            <>
              <label htmlFor="feedback-comment-input" className="sr-only">
                {t.feedbackLabel}
              </label>
              <textarea
                id="feedback-comment-input"
                rows={4}
                value={comment}
                onChange={(event) => {
                  setComment(
                    clampToMaxLength(event.target.value, QUICK_FEEDBACK_COMMENT_MAX_LENGTH)
                  )
                  setCommentError(null)
                }}
                onKeyDown={handleTextareaKeyDown}
                disabled={submitting}
                maxLength={QUICK_FEEDBACK_COMMENT_MAX_LENGTH}
                placeholder={t.feedbackPopoverPlaceholder}
                className="input-field min-h-[88px] w-full resize-none text-sm"
              />
              <p className="mt-1 text-right text-[10px] font-semibold text-slate-600 dark:text-[var(--text-tertiary)]">
                {comment.length} / {QUICK_FEEDBACK_COMMENT_MAX_LENGTH}
              </p>
              {commentError ? (
                <p className="mt-1 text-xs text-[var(--error)]" role="alert">
                  {commentError}
                </p>
              ) : null}
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={submitting || !comment.trim()}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-2 text-xs font-semibold text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] disabled:pointer-events-none disabled:opacity-50"
              >
                <SendIcon className="h-4 w-4" />
                {t.feedbackSubmit}
              </button>
            </>
          ) : (
            <div>
              <ul className="max-h-72 divide-y divide-[var(--border-color)] overflow-y-auto">
                {(history ?? []).map((row) => (
                  <li key={row.id} className="py-2 first:pt-0 last:pb-0">
                    <p className="flex items-center justify-end gap-1 text-xs font-medium text-[var(--text-secondary)]">
                      <span aria-hidden>🔒</span>
                      {t.feedbackReadOnly}
                    </p>
                    <p className="mt-1 line-clamp-2 text-sm font-medium text-[var(--text-primary)]">
                      {row.comment}
                    </p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-[var(--text-secondary)]">
                      <span aria-hidden>🕐</span>
                      {formatDateTime(row.createdAt, locale)}
                      {row.meetingTitle ? ` · ${row.meetingTitle}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setMode('compose')}
                className="mt-2 w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-2 text-xs font-semibold text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]"
              >
                {t.feedbackNewFeedback}
              </button>
            </div>
          )}
        </div>
      )}

      {!open && justSubmitted && (
        <p
          role="status"
          className={`absolute ${popoverPositionClass} z-[200] flex w-64 items-center gap-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-sm font-medium text-blue-600 shadow-[var(--shadow-sm)]`}
        >
          <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-emerald-400" />
          {t.feedbackSubmitSuccess}
        </p>
      )}

      {!open && !justSubmitted && submitFailed && (
        <div
          role="alert"
          className={`absolute ${popoverPositionClass} z-[200] flex w-64 items-start gap-2 rounded-lg bg-[var(--error)] px-3 py-2 text-sm font-medium text-white shadow-[var(--shadow-sm)]`}
        >
          <div className="flex-1">
            <p>{t.feedbackSubmitFailedRetry}</p>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              className="mt-1 font-semibold underline underline-offset-2"
            >
              {t.feedbackRetry}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setSubmitFailed(false)}
            aria-label={t.close}
            className="rounded p-0.5 hover:bg-white/20"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {!open && !justSubmitted && !submitFailed && hovering && (
        <div
          role="tooltip"
          className={`absolute top-1/2 ${tooltipPositionClass} z-[200] w-56 -translate-y-1/2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-xs font-medium text-blue-600 shadow-[var(--shadow-sm)]`}
        >
          {t.feedbackTooltip}
        </div>
      )}
    </div>,
    document.body
  )
}
